import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Card, CardContent } from '../components/ui/Card';
import { Heart, ShieldCheck, Scale, Users, ArrowRight, PlayCircle, Quote } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { CFA_INFO } from '../data/constants';
import { QualiopiTeaser } from '../components/QualiopiTeaser';
export function CFA() {
  return <div className="flex flex-col w-full" data-id="element-935">
      <PageHeader title="L'esprit Zillenium" subtitle="Plus qu'un CFA, un véritable tremplin vers votre avenir professionnel." data-id="element-936" />

      <div className="flex flex-col w-full" data-id="element-937">
        {/* Intro & CTA */}
        <section className="py-16 bg-white text-center" data-id="element-938">
          <div className="container max-w-3xl mx-auto" data-id="element-939">
            <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-primary-dark mb-6" data-id="element-940">
              L'alternance autrement.
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed mb-10" data-id="element-941">
              Chez Zillenium, nous ne formons pas de simples étudiants, mais les
              professionnels de demain. Notre pédagogie repose sur l'action, la
              confiance et un accompagnement sur-mesure.
            </p>
            <Button size="lg" className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-6 shadow-xl group" asChild data-id="element-942">
              <Link to="/candidater" className="flex items-center" data-id="element-943">
                Rejoindre Zillenium
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-944" />
              </Link>
            </Button>
          </div>
        </section>

        {/* Le Coeur du CFA : Justine & Le Directeur */}
        <section className="py-24 bg-muted relative overflow-hidden" data-id="element-945">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary rounded-full blur-[120px] opacity-10 pointer-events-none" data-id="element-946" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-947" />

          <div className="container max-w-6xl mx-auto relative z-10" data-id="element-948">
            <SectionHeading title="Ceux qui font Zillenium" subtitle="Découvrez la vision et l'énergie de notre équipe dirigeante et pédagogique." centered data-id="element-949" />

            <div className="grid md:grid-cols-2 gap-12 mt-16" data-id="element-950">
              {/* Le Directeur */}
              <Card className="border-none shadow-2xl bg-white overflow-hidden group hover:-translate-y-2 transition-transform duration-500" data-id="element-951">
                <div className="h-3 w-full bg-brand-orange" data-id="element-952" />
                <CardContent className="p-8 md:p-10 flex flex-col h-full" data-id="element-953">
                  <div className="mb-8 text-center" data-id="element-954">
                    <h3 className="font-extrabold font-heading text-3xl text-primary-dark mb-3" data-id="element-955">
                      {CFA_INFO.president}
                    </h3>
                    <Badge className="bg-lavande text-primary-dark hover:bg-lavande/80 border-none px-4 py-1.5 text-sm" data-id="element-956">
                      Directeur & Fondateur
                    </Badge>
                  </div>
                  <div className="relative rounded-[2rem] overflow-hidden bg-slate-100 aspect-[9/16] shadow-inner ring-4 ring-slate-50" data-id="element-957">
                    <iframe src="https://www.instagram.com/reel/DVOBbA0DIMk/embed" title="Mot du directeur" className="absolute inset-0 w-full h-full" frameBorder="0" scrolling="no" allowTransparency={true} data-id="element-958"></iframe>
                  </div>
                </CardContent>
              </Card>

              {/* Justine */}
              <Card className="border-none shadow-2xl bg-white overflow-hidden group hover:-translate-y-2 transition-transform duration-500" data-id="element-959">
                <div className="h-3 w-full bg-primary" data-id="element-960" />
                <CardContent className="p-8 md:p-10 flex flex-col h-full" data-id="element-961">
                  <div className="mb-8 text-center" data-id="element-962">
                    <h3 className="font-extrabold font-heading text-3xl text-primary-dark mb-3" data-id="element-963">
                      Justine Delille
                    </h3>
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-964">
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-4 py-1.5 text-sm" data-id="element-965">
                        Responsable pédagogique
                      </Badge>
                    </div>
                  </div>
                  <div className="relative rounded-[2rem] overflow-hidden bg-slate-100 aspect-[9/16] shadow-inner ring-4 ring-slate-50" data-id="element-966">
                    <iframe src="https://www.instagram.com/reel/DVQg1eADa3_/embed" title="Mot de la responsable pédagogique" className="absolute inset-0 w-full h-full" frameBorder="0" scrolling="no" allowTransparency={true} data-id="element-967"></iframe>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Mot du fondateur */}
        <section className="py-24 bg-white relative overflow-hidden" data-id="element-968">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-lavande rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-969" />
          <div className="container max-w-4xl mx-auto relative z-10" data-id="element-970">
            <div className="text-center mb-12" data-id="element-971">
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-brand-orange mb-4" data-id="element-972">
                Le mot du fondateur
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-primary-dark tracking-tight" data-id="element-973">
                L'alternance autrement
              </h2>
            </div>

            <Card className="border-none shadow-2xl bg-white overflow-hidden" data-id="element-974">
              <div className="h-2 w-full bg-brand-orange" data-id="element-975" />
              <CardContent className="p-8 md:p-14" data-id="element-976">
                <Quote className="w-12 h-12 text-lavande mb-6" data-id="element-977" />

                <p className="text-xl md:text-2xl font-bold font-heading text-primary-dark leading-snug mb-8" data-id="element-978">
                  Chez Zillenium Alternance, nous ne formons pas seulement des
                  apprenants : nous accompagnons des parcours de vie.
                </p>

                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed" data-id="element-979">
                  <p data-id="element-980">
                    L'alternance est une étape importante. Pour un jeune, c'est
                    souvent le premier vrai contact avec le monde professionnel,
                    ses exigences, ses codes, ses responsabilités, mais aussi
                    ses opportunités. Pour une entreprise, c'est un engagement
                    fort : accueillir, former, transmettre et faire grandir un
                    futur professionnel.
                  </p>
                  <p data-id="element-981">
                    Notre mission est de créer les conditions d'une
                    collaboration réussie entre trois acteurs essentiels :{' '}
                    <span className="font-semibold text-primary-dark" data-id="element-982">
                      l'apprenant, l'entreprise et le CFA.
                    </span>
                  </p>
                  <p data-id="element-983">
                    Nous accompagnons chaque jeune dans sa progression, sa
                    posture professionnelle, sa recherche d'entreprise, sa
                    confiance en lui et sa capacité à trouver sa place. Nous
                    savons que derrière chaque parcours, il peut y avoir des
                    doutes, des freins, des difficultés personnelles, sociales
                    ou psychologiques. C'est pourquoi notre accompagnement ne se
                    limite pas à la formation : il repose sur l'écoute, la
                    proximité, le suivi et la confiance.
                  </p>
                  <p data-id="element-984">
                    Nous accompagnons également les entreprises dans
                    l'intégration de leurs alternants, afin de sécuriser le
                    parcours, faciliter la communication et construire une
                    relation durable. Notre rôle est d'être un lien permanent
                    entre le jeune, l'employeur et l'équipe pédagogique.
                  </p>
                  <p data-id="element-985">
                    Chez Zillenium Alternance, nous sommes sur le terrain,
                    proches des jeunes, des familles, des entreprises et des
                    partenaires locaux. Nous comprenons les réalités de notre
                    territoire, les enjeux de l'insertion professionnelle et les
                    besoins d'accompagnement humain.
                  </p>
                  <p className="font-semibold text-primary-dark" data-id="element-986">
                    Rejoindre Zillenium Alternance, c'est intégrer un CFA qui
                    forme, accompagne et reste présent à chaque étape du
                    parcours.
                  </p>
                </div>

                <div className="mt-10 pt-8 border-t border-slate-100 flex items-center gap-4" data-id="element-987">
                  <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center text-white font-extrabold font-heading text-xl shrink-0" data-id="element-988">
                    SM
                  </div>
                  <div data-id="element-989">
                    <p className="font-extrabold font-heading text-lg text-primary-dark leading-tight" data-id="element-990">
                      Samir MOKEDDEM
                    </p>
                    <p className="text-brand-orange font-semibold text-sm" data-id="element-991">
                      Fondateur & Directeur — Zillenium Alternance
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Valeurs Visuelles (Compact) */}
        <section className="py-24 bg-white" data-id="element-992">
          <div className="container max-w-5xl mx-auto" data-id="element-993">
            <SectionHeading title="Notre ADN en 4 mots" centered className="mb-16" data-id="element-994" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-995">
              <div className="flex flex-col items-center text-center group" data-id="element-996">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-997">
                  <Heart className="w-10 h-10 text-primary" data-id="element-998" />
                </div>
                <h4 className="font-bold text-xl text-primary-dark mb-2" data-id="element-999">
                  Bienveillance
                </h4>
                <p className="text-sm text-muted-foreground" data-id="element-1000">
                  Un cadre humain et exigeant.
                </p>
              </div>
              <div className="flex flex-col items-center text-center group" data-id="element-1001">
                <div className="w-20 h-20 bg-brand-orange/10 rounded-3xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1002">
                  <ShieldCheck className="w-10 h-10 text-brand-orange" data-id="element-1003" />
                </div>
                <h4 className="font-bold text-xl text-primary-dark mb-2" data-id="element-1004">
                  Confiance
                </h4>
                <p className="text-sm text-muted-foreground" data-id="element-1005">
                  Valoriser chaque réussite.
                </p>
              </div>
              <div className="flex flex-col items-center text-center group" data-id="element-1006">
                <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1007">
                  <Scale className="w-10 h-10 text-primary" data-id="element-1008" />
                </div>
                <h4 className="font-bold text-xl text-primary-dark mb-2" data-id="element-1009">
                  Responsabilité
                </h4>
                <p className="text-sm text-muted-foreground" data-id="element-1010">
                  Devenir autonome et fiable.
                </p>
              </div>
              <div className="flex flex-col items-center text-center group" data-id="element-1011">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1012">
                  <Users className="w-10 h-10 text-primary" data-id="element-1013" />
                </div>
                <h4 className="font-bold text-xl text-primary-dark mb-2" data-id="element-1014">
                  Collectif
                </h4>
                <p className="text-sm text-muted-foreground" data-id="element-1015">
                  L'esprit d'équipe avant tout.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BDE Teaser */}
        <section className="py-16 bg-primary-dark text-white relative overflow-hidden" data-id="element-1016">
          <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1017" />
          <div className="container max-w-4xl mx-auto relative z-10" data-id="element-1018">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-white/5 p-8 md:p-12 rounded-[2rem] border border-white/10 backdrop-blur-sm" data-id="element-1019">
              <div className="text-center md:text-left" data-id="element-1020">
                <h3 className="font-extrabold font-heading text-3xl mb-4" data-id="element-1021">
                  La vie étudiante
                </h3>
                <p className="text-lavande text-lg max-w-md" data-id="element-1022">
                  Découvrez le BDE Zillenium : des projets solidaires, des
                  événements fédérateurs et une ambiance unique.
                </p>
              </div>
              <Button size="lg" className="bg-white hover:bg-lavande text-primary-dark text-lg px-8 shrink-0 group" asChild data-id="element-1023">
                <Link to="/bde" className="flex items-center" data-id="element-1024">
                  Découvrir le BDE
                  <PlayCircle className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" data-id="element-1025" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Qualiopi */}
        <section className="py-24 bg-lavande relative overflow-hidden" data-id="element-1026">
          <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white rounded-full blur-[120px] opacity-60 pointer-events-none" data-id="element-1027" />
          <div className="container max-w-6xl relative z-10" data-id="element-1028">
            <QualiopiTeaser data-id="element-1029" />
          </div>
        </section>
      </div>
    </div>;
}